import { Suspense, useEffect } from 'react';
import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import ErrorBoundary from '@components/shared/ErrorBoundary/ErrorBoundary';
import RouteSkeleton from '@components/shared/RouteSkeleton/RouteSkeleton';
import { getTenantById } from '@/services/tenantService';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingDismissed } from '@/features/onboarding/useOnboardingDismissed';
import { canAccessRoute, getFirstAccessibleRoute } from '@/lib/routePermissions';

export const Route = createFileRoute('/t')({
  component: TenantLayout,
});

function TenantLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const organizationType = useAuthStore((s) => s.organizationType);
  const userType = useAuthStore((s) => s.userType);
  const permissions = useAuthStore((s) => s.permissions);
  const sessionTenantStatus = useAuthStore((s) => s.session?.tenant.status ?? null);
  const tenantId = useAuthStore((s) => s.session?.tenant.id ?? '');
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canViewTenant = hasPermission('TENANTS_VIEW') || hasPermission('TENANT_PROFILE_VIEW');
  const dismissed = useOnboardingDismissed((s) => s.dismissed);

  // Org type guard — only TENANT non-employee users can access /t routes
  useEffect(() => {
    if (!organizationType) return;
    if (userType === 'EMPLOYEE' || organizationType !== 'TENANT') {
      void navigate({ to: '/', replace: true });
    }
  }, [organizationType, userType, navigate]);

  // Narrow live read: Redis session is frozen at login per BE design, so when
  // the stamped status is ONBOARDING we need a DB read to detect a mid-session
  // activation surviving across a page refresh. `dismissed` is in-memory only
  // and resets on refresh, so it alone can't carry the user past this gate.
  const { data: liveTenant, isPending: liveTenantPending } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenantById(tenantId),
    enabled: !!tenantId && sessionTenantStatus === 'ONBOARDING',
    staleTime: 30_000,
  });

  const effectiveStatus =
    sessionTenantStatus === 'ONBOARDING' && liveTenant
      ? liveTenant.tenant.status
      : sessionTenantStatus;
  const fetchInFlight =
    sessionTenantStatus === 'ONBOARDING' && canViewTenant && liveTenantPending;

  // The two onboarding paths are the redirect TARGETS — without this guard the
  // user would be redirected away from them on direct URL entry and bounce
  // back, looping forever. The choice page also has no other way in besides
  // navigation from the form stepper or direct URL, so it must be reachable.
  const onOnboardingPath =
    pathname === '/t/requested-details' || pathname === '/t/contract-review-choice';

  const shouldRedirect =
    !fetchInFlight
    && effectiveStatus === 'ONBOARDING'
    && !dismissed
    && !onOnboardingPath;

  useEffect(() => {
    if (shouldRedirect) {
      void navigate({ to: '/t/requested-details' });
    }
  }, [shouldRedirect, navigate]);

  // Route permission guard — getFirstAccessibleRoute floors at /t/home so the
  // redirect target is always defined.
  useEffect(() => {
    if (permissions.length === 0 || shouldRedirect) return;

    if (!canAccessRoute(pathname, permissions)) {
      void navigate({ to: getFirstAccessibleRoute('/t', permissions), replace: true });
    }
  }, [pathname, permissions, navigate, shouldRedirect]);

  // Block render until the auth store is hydrated — see w.tsx for the
  // same-frame leak rationale.
  if (!organizationType || !userType) return null;
  if (organizationType !== 'TENANT' || userType === 'EMPLOYEE') return null;

  // Hold child render until we know whether to redirect — children would
  // otherwise mount on the stale session value and route to the wrong place.
  if (fetchInFlight) return null;
  if (shouldRedirect) return null;

  return (
    <ErrorBoundary>
      <Suspense fallback={<RouteSkeleton />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
}
